import React, { useEffect, useMemo, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { Plus, X, Search, Filter, Trash2, UserCheck, Flag, Hash, MessageSquare, Phone, Mail, Calendar, Building2 } from "lucide-react";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";

const STAGES = [
  { v: "new", l: "New" },
  { v: "follow_up", l: "Follow Up" },
  { v: "qualified", l: "Qualified" },
  { v: "closed", l: "Closed" },
];

const CATS = [
  { v: "general", l: "General" },
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "dental", l: "Dental Practice" },
  { v: "medical", l: "Medical Clinic" },
];

const SOURCES = [
  { v: "website", l: "Website" },
  { v: "call", l: "Call-in" },
  { v: "referral", l: "Referral" },
  { v: "event", l: "Event" },
  { v: "email", l: "Email" },
  { v: "social", l: "Social Media" },
  { v: "walk_in", l: "Walk-in" },
  { v: "other", l: "Other" },
];

const PRIORITIES = [
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-700" },
  { v: "medium", l: "Medium", cls: "bg-blue-100 text-blue-800" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-800" },
  { v: "urgent", l: "Urgent", cls: "bg-rose-100 text-rose-800" },
];

const STAGE_COLOR = {
  new: "bg-slate-100 text-slate-700",
  follow_up: "bg-amber-100 text-amber-800",
  qualified: "bg-emerald-100 text-emerald-800",
  closed: "bg-navy text-white",
};

const CAT_COLOR = {
  residential: "bg-blue-50 text-blue-700",
  commercial: "bg-slate-100 text-slate-700",
  dental: "bg-emerald-50 text-emerald-700",
  medical: "bg-rose-50 text-rose-700",
  general: "bg-slate-50 text-slate-600",
};

const KIND_ICONS = { note: MessageSquare, call: Phone, email: Mail, meeting: Calendar, status: Building2 };

const EMPTY_FORM = {
  name: "", email: "", phone: "", company: "",
  source: "website", referral_source: "",
  category: "general", priority: "medium",
  budget_min: "", budget_max: "", timeline: "",
  location_preference: "",
  notes: "", stage: "new",
};

const fmtRel = (iso) => {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const load = () => {
    const params = stageFilter ? { stage: stageFilter } : {};
    api.get("/crm/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  };
  const loadUsers = () => api.get("/crm/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => { load(); }, [stageFilter]); // eslint-disable-line
  useEffect(() => { loadUsers(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min === "" ? null : Number(form.budget_min),
        budget_max: form.budget_max === "" ? null : Number(form.budget_max),
      };
      await api.post("/crm/leads", payload);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch {}
  };

  const updateStage = async (id, stage) => {
    await api.patch(`/crm/leads/${id}`, { stage });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/crm/leads/${id}`);
    load();
  };

  const filtered = leads.filter((l) => {
    if (catFilter && (l.category || l.interest) !== catFilter) return false;
    if (q) {
      const t = q.toLowerCase();
      return [l.name, l.email, l.phone, l.company, l.location_preference, l.referral_source]
        .some((v) => (v || "").toLowerCase().includes(t));
    }
    return true;
  });

  const toggleCheck = (id) => {
    const next = new Set(checked);
    next.has(id) ? next.delete(id) : next.add(id);
    setChecked(next);
  };
  const toggleAll = () => {
    if (checked.size === filtered.length) setChecked(new Set());
    else setChecked(new Set(filtered.map((l) => l.id)));
  };

  const bulkUpdate = async (patch) => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    await api.post("/crm/leads/bulk", { ids, ...patch });
    setChecked(new Set());
    load();
  };

  const bulkDelete = async () => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length > 1 ? "s" : ""}? This is permanent.`)) return;
    await api.post("/crm/leads/bulk-delete", { ids });
    setChecked(new Set());
    load();
  };

  return (
    <CRMLayout>
      <div className="flex items-end justify-between mb-6" data-testid="crm-leads-page">
        <div>
          <div className="overline text-gold">Pipeline</div>
          <h1 className="font-display text-3xl text-navy mt-2">Leads</h1>
          <div className="text-sm text-slate-500 mt-1">{filtered.length} of {leads.length} leads</div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-navy text-sm py-2.5 px-4" data-testid="leads-add-btn"><Plus className="w-4 h-4" /> Add Lead</button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company, location…"
            className="w-full border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-gold"
            data-testid="leads-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-500 uppercase tracking-widest">Stage</span>
          <button onClick={() => setStageFilter("")} className={`text-xs px-3 py-1.5 border ${stageFilter === "" ? "border-gold bg-slate-50 text-navy" : "border-slate-200 text-slate-600"}`} data-testid="leads-filter-all">All</button>
          {STAGES.map((s) => (
            <button key={s.v} onClick={() => setStageFilter(s.v)} className={`text-xs px-3 py-1.5 border ${stageFilter === s.v ? "border-gold bg-slate-50 text-navy" : "border-slate-200 text-slate-600"}`} data-testid={`leads-filter-${s.v}`}>{s.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 uppercase tracking-widest">Category</span>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="text-xs border border-slate-200 px-2 py-1.5 bg-white" data-testid="leads-filter-cat">
            <option value="">All</option>
            {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 && (
        <div className="bg-navy text-white p-3 flex flex-wrap items-center gap-3 mb-4" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold"><Hash className="w-4 h-4 inline -mt-1" /> {checked.size} selected</span>
          <div className="inline-flex items-center gap-2 ml-4">
            <UserCheck className="w-4 h-4 text-gold" />
            <select onChange={(e) => e.target.value && bulkUpdate({ assigned_agent_id: e.target.value })} defaultValue="" className="bg-navy-light text-white border border-white/20 px-2 py-1.5 text-xs" data-testid="bulk-assign-select">
              <option value="">Assign agent…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div className="inline-flex items-center gap-2">
            <Flag className="w-4 h-4 text-gold" />
            <select onChange={(e) => e.target.value && bulkUpdate({ stage: e.target.value })} defaultValue="" className="bg-navy-light text-white border border-white/20 px-2 py-1.5 text-xs" data-testid="bulk-stage-select">
              <option value="">Change stage…</option>
              {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div className="inline-flex items-center gap-2">
            <select onChange={(e) => e.target.value && bulkUpdate({ priority: e.target.value })} defaultValue="" className="bg-navy-light text-white border border-white/20 px-2 py-1.5 text-xs" data-testid="bulk-priority-select">
              <option value="">Set priority…</option>
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <button onClick={bulkDelete} className="ml-auto inline-flex items-center gap-1.5 text-xs text-rose-300 hover:text-rose-100 px-2 py-1.5" data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={() => setChecked(new Set())} className="text-xs text-white/60 hover:text-white" data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-visible">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-3 w-10">
                <input type="checkbox" checked={filtered.length > 0 && checked.size === filtered.length} onChange={toggleAll} data-testid="bulk-select-all" />
              </th>
              <th className="text-left p-3">Lead</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Priority</th>
              <th className="text-left p-3">Assigned</th>
              <th className="text-left p-3">Stage</th>
              <th className="text-left p-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const p = PRIORITIES.find((x) => x.v === (l.priority || "medium")) || PRIORITIES[1];
              const agent = usersById[l.assigned_agent_id];
              return (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.has(l.id)} onChange={() => toggleCheck(l.id)} data-testid={`lead-check-${l.id}`} />
                  </td>
                  <td className="p-3">
                    <div className="relative group inline-block">
                      <button onClick={() => setSelected(l.id)} className="text-left" data-testid={`lead-name-${l.id}`}>
                        <div className="font-semibold text-navy group-hover:text-gold transition">{l.name}</div>
                        <div className="text-xs text-slate-400">
                          {l.email}{l.company ? ` · ${l.company}` : ""}
                          {l.notes_count > 0 && <span className="ml-2 text-gold">· {l.notes_count} ×</span>}
                        </div>
                      </button>
                      {/* Timeline hover popover */}
                      {l.recent_notes && l.recent_notes.length > 0 && (
                        <div className="absolute left-0 top-full mt-2 z-40 w-80 bg-white border border-navy-light shadow-luxury-lg p-4 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-opacity pointer-events-none" data-testid={`lead-timeline-preview-${l.id}`}>
                          <div className="overline text-gold mb-2">Latest activity</div>
                          <div className="space-y-2">
                            {l.recent_notes.map((n) => {
                              const Icon = KIND_ICONS[n.kind] || MessageSquare;
                              return (
                                <div key={n.id} className="flex gap-2 text-xs">
                                  <Icon className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-navy line-clamp-2">{n.body}</div>
                                    <div className="text-slate-400 mt-0.5">{n.author_name} · {fmtRel(n.created_at)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 uppercase tracking-wider font-semibold ${CAT_COLOR[l.category || l.interest] || CAT_COLOR.general}`}>
                      {l.category || l.interest || "general"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div>{(SOURCES.find((s) => s.v === l.source) || {}).l || l.source}</div>
                    {l.referral_source && <div className="text-xs text-slate-400">{l.referral_source}</div>}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 uppercase tracking-wider font-semibold ${p.cls}`}>{p.l}</span>
                  </td>
                  <td className="p-3 text-slate-600 text-xs">
                    {agent ? agent.name : <span className="text-slate-400">Unassigned</span>}
                  </td>
                  <td className="p-3">
                    <select value={l.stage} onChange={(e) => updateStage(l.id, e.target.value)} className={`text-xs px-2 py-1 border-0 font-semibold uppercase tracking-wider ${STAGE_COLOR[l.stage] || STAGE_COLOR.new}`} data-testid={`lead-stage-${l.id}`}>
                      {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <button onClick={() => setSelected(l.id)} className="text-xs text-navy hover:text-gold mr-3" data-testid={`lead-open-${l.id}`}>Open</button>
                    <button onClick={() => del(l.id)} className="text-xs text-red-500 hover:text-red-700" data-testid={`lead-delete-${l.id}`}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create Lead Modal */}
      {open && (
        <div className="fixed inset-0 bg-navy/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
              <div>
                <div className="overline text-gold">New</div>
                <h2 className="font-display text-2xl text-navy mt-1">Add Lead</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-navy" data-testid="lead-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="px-8 py-6 space-y-6" data-testid="lead-create-form">
              <Section title="Contact">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Full name" required><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-name" /></FF>
                  <FF label="Email" required><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-email" /></FF>
                  <FF label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-phone" /></FF>
                  <FF label="Company / Practice"><input placeholder="(optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-company" /></FF>
                </div>
              </Section>

              <Section title="Classification">
                <div className="grid md:grid-cols-3 gap-4">
                  <FF label="Category" required>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-form-category">
                      {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                  </FF>
                  <FF label="Priority">
                    <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-form-priority">
                      {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                  </FF>
                  <FF label="Initial stage">
                    <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-form-stage">
                      {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </FF>
                </div>
              </Section>

              <Section title="Source">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="How did they find us?">
                    <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-form-source">
                      {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </FF>
                  <FF label="Referrer / Campaign (optional)">
                    <input placeholder="e.g. Dr. Rao, LinkedIn, Google Ads" value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-referral" />
                  </FF>
                </div>
              </Section>

              <Section title="Requirements">
                <div className="grid md:grid-cols-3 gap-4">
                  <FF label="Budget Min ($)">
                    <input type="number" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-budget-min" />
                  </FF>
                  <FF label="Budget Max ($)">
                    <input type="number" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-budget-max" />
                  </FF>
                  <FF label="Timeline">
                    <select value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-form-timeline">
                      <option value="">— Select —</option>
                      <option value="< 3 months">&lt; 3 months</option>
                      <option value="3-6 months">3–6 months</option>
                      <option value="6-12 months">6–12 months</option>
                      <option value="12+ months">12+ months</option>
                    </select>
                  </FF>
                </div>
                <div className="mt-4">
                  <FF label="Location preference">
                    <input placeholder="e.g. Austin, TX or Downtown Manhattan" value={form.location_preference} onChange={(e) => setForm({ ...form, location_preference: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-location" />
                  </FF>
                </div>
              </Section>

              <Section title="Initial note">
                <textarea rows="3" placeholder="What's the context? This becomes the first timeline entry." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-form-notes" />
              </Section>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-500 px-4 py-2">Cancel</button>
                <button type="submit" className="btn-navy text-sm py-2.5 px-6" data-testid="lead-form-submit">Create Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <LeadDetailDrawer leadId={selected} onClose={() => setSelected(null)} onUpdate={load} />
      )}
    </CRMLayout>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="overline text-gold mb-3">{title}</div>
    {children}
  </div>
);

const FF = ({ label, required, children }) => (
  <div>
    <label className="block text-xs text-slate-500 uppercase tracking-widest mb-1.5">
      {label}{required && <span className="text-gold">*</span>}
    </label>
    {children}
  </div>
);

export default Leads;
